/*
  Get View CRQ powers:

  The aim of this files is to break down the complicated logic of when and who can do things to a CRQ.

  There are essentially two steps to figuring out what powers the user has. The first step is to essentially find out what
  overal powers the user has by asking for each overal power. We do this by asking:

  "is the CRQ in the correct state (isCorrectState)"
  and
  "is the user the correct person to edit it (isCorrectPerson)"

  The second step is to the break down those overal powers into individual powers
*/


import {
  isStatusCoordinator,
  isStatusPeerReview,
  isStatusApprovals,
  isStatusImplementation,
  isStatusCompleted,
  isStatusClosed,
} from 'AppSrc/core/crq/crq-statuses';
import { COORDINATOR } from 'AppSrc/core/components/forms/form-base-components/data-structure/constants';
// import { FIXED, MOBILE } from 'AppSrc/core/components/forms/value-dependency-constants';
import { START_AND_END_DATE } from 'AppSrc/core/components/forms/form-types/create-crq/section-three/section-three';
import {
  userRoleForSelectedNetwork,
} from 'AppSrc/core/components/forms/form-types/create-crq/common-logic';
// import { COORDINATOR } from 'AppSrc/core/components/forms/form-base-components/data-structure/constants';
// import { START_AND_END_DATE } from 'AppSrc/core/components/forms/form-types/create-crq/section-three/section-three';
import moment from 'moment';


const getApprovalGroupsUserActionable = (approvalHistory) => approvalHistory.filter((historyItem) => historyItem.actionable);


const getHasCoordinatorReviewPowers = (remedyId, formData) => {

  const {
    status,
    coordinatorId,
  } = formData;

  const isCorrectState = isStatusCoordinator(status);
  const isCorrectPerson = coordinatorId && (remedyId === coordinatorId);


  return (isCorrectState && isCorrectPerson);
};

const getHasCoordinatorCompletionReviewPowers = (userRoles, remedyId, formData) => {
  const {
    status,
    coordinatorId,
  } = formData;
  const userRole = userRoleForSelectedNetwork(formData, userRoles);
  const scheduledEndDate = formData.dataByFormSectionId[START_AND_END_DATE].dataByFieldId['scheduled-end-date'].value;
  const isPastScheduledEndTime = moment(new Date()).diff(scheduledEndDate) > 0;
  const isCoordinator = userRole === COORDINATOR;
  const isAssignedCoordinator = isCoordinator && (remedyId === coordinatorId);
  const isCorrectState = isStatusImplementation(status);
  const isCompletedOrClosed = isStatusCompleted(status) || isStatusClosed(status);

  return (isAssignedCoordinator && isCorrectState && isPastScheduledEndTime) || isCompletedOrClosed;
  // const scheduledEndDate = formData.dataByFormSectionId[START_AND_END_DATE].dataByFieldId['scheduled-end-date'].value;
  // const isPastScheduledEndTime = moment(new Date()).diff(scheduledEndDate) > 0;
  // const isCoordinator = (userRoles[MOBILE] === COORDINATOR || userRoles[FIXED] === COORDINATOR);
  // const isAssignedCoordinator = isCoordinator && (remedyId === coordinatorId);
  // const isCorrectState = isStatusImplementation(status);

  // return (isAssignedCoordinator && isCorrectState && isPastScheduledEndTime);
};

const getHasPeerReviewPowers = (remedyId, formData) => {

  const {
    status,
    peerReviewerId,
  } = formData;

  const isCorrectState = isStatusPeerReview(status);
  const isCorrectPerson = peerReviewerId && (remedyId === peerReviewerId) ? true : false;


  return (isCorrectState && isCorrectPerson);
};

const getHasApproverPowers = (formData) => {
  const {
    status,
    approvalHistory,
  } = formData;
  const approvalGroupsUserActionable = getApprovalGroupsUserActionable(approvalHistory);

  const isCorrectState = isStatusApprovals(status);
  const isCorrectPerson = approvalGroupsUserActionable.length > 0;

  return (isCorrectState && isCorrectPerson);
};

// the assigned coordinator for a crq can add extra approvers if the crq is in approvals stage
const getHasAddApproversPowers = (remedyId, formData) => {

  const {
    status,
    coordinatorId,
  } = formData;

  const isCorrectState = isStatusApprovals(status);
  const isCorrectPerson = coordinatorId && (remedyId === coordinatorId);

  return (isCorrectState && isCorrectPerson);
};


// combines a bunch of functions for ease of use on the view crq summary page
const getViewCrqPowers = (params) => {
  const {
    remedyId,
    formData,
    isChangeAnalyst,
    userRoles,
    sectionId, // Retrieving section id to give access to edit section 7 for adding additional approvers in form by authorised coordinators.
  } = params;

  const hasCoordinatorReviewPowers = getHasCoordinatorReviewPowers(remedyId, formData);
  const hasPeerReviewPowers = getHasPeerReviewPowers(remedyId, formData);
  const hasApproverPowers = getHasApproverPowers(formData);
  const hasCoordinatorCompletionReviewPowers = getHasCoordinatorCompletionReviewPowers(userRoles, remedyId, formData);
  const canAddApprovers = getHasAddApproversPowers(remedyId, formData);
  const statusReason = formData.statusReason;// Using the statusreason For CircuitCheck

  // canEditForm is always true for circuitCheck feature ChangeAnalyst
  // CanEditForm will be true for coordinators only to add additional approvers by clicking the button on approvalshistory tab.
  const canEditForm = (isChangeAnalyst === true && statusReason === '10500') || (canAddApprovers && sectionId === 'APPROVERS_SECTION') ? true : hasCoordinatorReviewPowers;
  // const canEditForm = (isChangeAnalyst === true && statusReason === '10500') ? true : hasCoordinatorReviewPowers;
  const canViewErrors = hasCoordinatorReviewPowers;
  // canViewSectionsRead is always true for ChangeAnalyst statusReason 10500
  const canViewSectionsRead = hasCoordinatorReviewPowers || (hasPeerReviewPowers && statusReason !== '10500') || hasApproverPowers || (isChangeAnalyst === true && statusReason === '10500');
  // canSubmitForm is always true for ChangeAnalyst and statusReason 10500
  const canSubmitForm = hasCoordinatorReviewPowers || (hasPeerReviewPowers && statusReason !== '10500') || hasApproverPowers || (isChangeAnalyst === true && statusReason === '10500');
  const canViewCompletionReview = hasCoordinatorCompletionReviewPowers;

  return ({
    canEditForm,
    canViewErrors,
    canViewSectionsRead,
    canSubmitForm,
    canAddApprovers,
    canViewCompletionReview,
  });
};

export {
  getViewCrqPowers,
  getApprovalGroupsUserActionable,
};

